import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../utils/api";
import { UserPlus, User2 } from "lucide-react";
import UserModal from "./UserModal";
import {
  Card,
  Button,
  PageHeader,
  AdminLayout,
  Badge,
  LoadingSpinner,
  EmptyState,
  useToast,
} from "../components/AdminSharedComponents";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const response = await api.get("/api/users");
        setUsers(response.data.data || []);
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
        toast.error("Gagal memuat data user");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Manage scroll lock when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  const openModal = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedUser(null);
    setIsModalOpen(false);
  };

  const handleAddUser = () => {
    navigate("/admin/users/add");
  };

  const handleEditUser = (user) => {
    navigate(`/admin/users/edit/${user.id}`);
    closeModal();
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await api.delete(`/api/users/${userId}`);
      if (response.status === 204) {
        setUsers(users.filter((user) => user.id !== userId));
        closeModal();
        toast.success("User berhasil dihapus");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Gagal menghapus user. Silakan coba lagi.");
    }
  };

  // Function to get role badge variant
  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case "superadmin":
        return "danger";
      case "admin_produk":
        return "warning";
      case "admin_proyek":
        return "info";
      case "spv":
        return "success";
      case "client":
        return "secondary";
      default:
        return "primary";
    }
  };

  // Function to format role display name
  const formatRoleName = (role) => {
    switch (role) {
      case "superadmin":
        return "Super Admin";
      case "admin_produk":
        return "Admin Produk";
      case "admin_proyek":
        return "Admin Proyek";
      case "spv":
        return "Supervisor";
      case "client":
        return "Client";
      default:
        return role;
    }
  };

  // Function to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Users"
        subtitle="Manage user accounts and permissions"
        actions={
          <Button variant="primary" onClick={handleAddUser}>
            <div className="flex items-center">
              <UserPlus size={18} className="mr-2" />
              Add User
            </div>
          </Button>
        }
      />

      <Card title="User Directory" accentColor="dark">
        {loading ? (
          <LoadingSpinner message="Memuat data user..." />
        ) : users.length > 0 ? (
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {users.map((user) => (
              <div
                key={user.id}
                onClick={() => openModal(user)}
                className="bg-white rounded-lg overflow-hidden shadow-md border border-gray-100 cursor-pointer transition-all duration-200 hover:scale-102 hover:shadow-lg hover:border-gray-200"
              >
                <div className="w-full h-48 bg-gray-100 flex items-center justify-center overflow-hidden">
                  {/* User avatar placeholder */}
                  <div className="rounded-full bg-gray-200 w-16 h-16 flex items-center justify-center text-primary">
                    <User2 className="w-8 h-8" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-1 truncate text-primary-dark">
                    {user.name}
                  </h3>
                  <p className="truncate text-sm text-gray-600 mb-2">
                    {user.email}
                  </p>
                  <Badge variant={getRoleBadgeVariant(user.role)}>
                    {formatRoleName(user.role)}
                  </Badge>
                  <div className="mt-2 text-xs text-gray-500">
                    Joined: {formatDate(user.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            message="Belum ada user. Klik 'Add User' untuk membuat user baru."
            action={{
              label: "Add User",
              onClick: handleAddUser,
            }}
          />
        )}
      </Card>

      {isModalOpen && selectedUser && (
        <UserModal
          user={selectedUser}
          onClose={closeModal}
          onEdit={handleEditUser}
          onDelete={handleDeleteUser}
          formatRoleName={formatRoleName}
          formatDate={formatDate}
        />
      )}
    </AdminLayout>
  );
};

export default Users;
